/**
 * Deterministic, provider-disconnected display fixtures for the Agent
 * Workspace presentation layer only. Values are static and representative —
 * never fetched, persisted, mutated, or presented as live data.
 */

export const KPI_ITEMS = Object.freeze([
  { key: "talk-time", label: "Talk Time", value: "18:24" },
  { key: "active-time", label: "Active Time", value: "1:12:58" },
  { key: "submitted-applications", label: "Submitted Applications", value: "2" },
  { key: "scheduled-appointments", label: "Scheduled Appointments", value: "3" },
  { key: "dialable-leads", label: "Dialable Leads", value: "247" },
]);

// Agent Toolbar tool keys, in the standardized MissionAssist domain-language
// order established for the toolbar rail. LEAD = information already on the
// lead record before/independent of this conversation. DISCOVERY =
// information collected or confirmed during this conversation — the same
// "Discovery" concept that later lives on the Appointment record. Keeping
// the two conceptually and nominally distinct is intentional.
export const RIGHT_TOOL_KEYS = Object.freeze({
  LEAD: "lead",
  DISCOVERY: "discovery",
  QUOTE: "quote",
  CALCULATOR: "calculator",
  CALENDAR: "calendar",
  ACTIONS: "actions",
  NOTES: "notes",
  HISTORY: "history",
});

export const BOTTOM_CONTROL_KEYS = Object.freeze({
  HOLD: "hold",
  MUTE: "mute",
  KEYPAD: "keypad",
  VOICEMAIL: "voicemail",
  HANG_UP: "hang-up",
  TRANSFER: "transfer",
  CONFERENCE: "conference",
  ADD_CALL: "add-call",
});

// Deterministic, clearly-synthetic customer-record fixture for the Agent
// Toolbar's Lead tool. Mirrors the customer-information/address/
// lead-information shape of a dialer-style lead record, rebuilt for the
// Workspace's own presentation — never fetched, persisted, mutated, or
// presented as a real prospect. Field values are intentionally generic
// placeholders (never realistic personal data). This is information that
// existed on the lead record before/independent of the current
// conversation — see RIGHT_TOOL_KEYS.LEAD vs RIGHT_TOOL_KEYS.DISCOVERY.
export const LEAD_FIXTURE = Object.freeze({
  customerInformation: Object.freeze({
    firstName: "[First]",
    middleInitial: "M",
    lastName: "[Last]",
    gender: "Not Specified",
    dob: "01/01/1970",
    phone: "(000) 000-0000",
    alternatePhone: "(000) 000-0001",
    email: "prospect@example.com",
  }),
  address: Object.freeze({
    address1: "123 Example Street",
    address2: "",
    address3: "",
    city: "[City]",
    state: "[State]",
    postalCode: "00000",
    province: "",
  }),
  leadInformation: Object.freeze({
    vendorId: "VEND-000000",
    leadId: "LEAD-000000",
    comments:
      "Placeholder comments field — reflects data typically supplied at lead list upload (e.g., source notes, campaign codes, or intake remarks).",
  }),
});

// Deterministic, clearly-synthetic Discovery fixture for the Agent
// Toolbar's Discovery tool. Mirrors the REAL eventual data contract
// documented in WORKSPACE-AUDIT-002 and WORKSPACE-AUDIT-003
// (docs/audits/workspace/) rather than a Workspace-specific invention.
// Discovery's canonical relationship is Field Definition + Subject ->
// Value, and the subject list is dynamic: one synthetic household
// pseudo-subject plus N prospect subjects (mirroring the real
// household + run-prospects model), not a fixed two-section split:
//   - `subjects` mirrors the real dynamic subject list a run exposes —
//     the household pseudo-subject plus every prospect participating in
//     the conversation. `kind` distinguishes the one household entry from
//     the N prospect entries; prospect entries carry `sort_order` so their
//     display order is explicit and data-driven, never assumed from array
//     position.
//   - `fields` mirrors the backend `form_fields` shape
//     (id, label, field_type, required, scope, sort_order) — the same
//     field *definitions* Script Builder authors and Script Runner/
//     Appointment Discovery both read. field_type is restricted to the
//     real supported vocabulary: text, phone, email, textarea, checkbox,
//     dropdown, number. `scope` is only ever "household" or "prospects" —
//     never a per-prospect scope such as "prospect_1"; which prospect an
//     answer belongs to is expressed by `values[].subject_id`, not by the
//     field definition.
//   - `values` mirrors the REAL `run_data` shape (an array of
//     {field_id, subject_id, value} entries, not a flat object) — the same
//     shape the real /runs/{id}/data and /appointments/{id}/discovery
//     endpoints return. A field with no matching {field_id, subject_id}
//     entry is unanswered for that subject. Two prospects can hold
//     independent values for the SAME prospects-scoped field_id simply by
//     each having their own {field_id, subject_id} entry — there is only
//     ever one field definition per field_id.
// Discovery = information collected/confirmed DURING the Run — distinct
// from LEAD_FIXTURE, which is information already on the lead record
// before/independent of the conversation. This fixture intentionally does
// NOT hardcode business field names into any renderer; the same generic
// {subjects, fields, values} shape could describe an entirely different
// script's field set and prospect list without any component change. Two
// synthetic prospects (John Jason, Mary Jason) are used so the subject
// filter can demonstrate genuine N-subject, independent-value behavior —
// the rendering logic itself is not written against a fixed count of two.
// Fields span the complete real field_type vocabulary (text, phone, email,
// textarea, checkbox, dropdown, number) so the editable-control mapping has
// a real example of every supported type to render against.
export const DISCOVERY_FIXTURE = Object.freeze({
  subjects: Object.freeze([
    Object.freeze({ id: "household", name: "Household", kind: "household" }),
    Object.freeze({ id: "prospect-john", name: "John Jason", kind: "prospect", sort_order: 0 }),
    Object.freeze({ id: "prospect-mary", name: "Mary Jason", kind: "prospect", sort_order: 1 }),
  ]),
  fields: Object.freeze([
    Object.freeze({
      id: "field-household-income",
      label: "Household Income",
      field_type: "number",
      required: true,
      scope: "household",
      sort_order: 1,
    }),
    Object.freeze({
      id: "field-monthly-budget",
      label: "Monthly Budget",
      field_type: "number",
      required: true,
      scope: "household",
      sort_order: 2,
    }),
    Object.freeze({
      id: "field-goal-of-insurance",
      label: "Goal of Insurance",
      field_type: "dropdown",
      required: false,
      scope: "household",
      sort_order: 3,
      options: ["Protect Family", "Cover Final Expenses", "Leave a Legacy"],
    }),
    Object.freeze({
      id: "field-household-notes",
      label: "Household Notes",
      field_type: "textarea",
      required: false,
      scope: "household",
      sort_order: 4,
    }),
    Object.freeze({
      id: "field-existing-coverage",
      label: "Existing Coverage",
      field_type: "number",
      required: true,
      scope: "prospects",
      sort_order: 5,
    }),
    Object.freeze({
      id: "field-tobacco-use",
      label: "Tobacco Use",
      field_type: "checkbox",
      required: true,
      scope: "prospects",
      sort_order: 6,
    }),
    Object.freeze({
      id: "field-annual-income",
      label: "Annual Income",
      field_type: "number",
      required: false,
      scope: "prospects",
      sort_order: 7,
    }),
    Object.freeze({
      id: "field-contact-phone",
      label: "Best Contact Phone",
      field_type: "phone",
      required: false,
      scope: "prospects",
      sort_order: 8,
    }),
    Object.freeze({
      id: "field-contact-email",
      label: "Contact Email",
      field_type: "email",
      required: false,
      scope: "prospects",
      sort_order: 9,
    }),
    Object.freeze({
      id: "field-callback-window",
      label: "Preferred Callback Window",
      field_type: "text",
      required: false,
      scope: "prospects",
      sort_order: 10,
    }),
  ]),
  // No entry for a given {field_id, subject_id} pair means unanswered.
  // "field-callback-window" (both prospects) and "field-contact-email" for
  // Mary Jason are intentionally left unanswered to demonstrate that an
  // editable control for an unanswered field renders genuinely empty, not
  // a placeholder value. "field-existing-coverage" deliberately includes a
  // literal "0" value for Mary Jason to demonstrate zero is a legitimate,
  // displayed/editable value rather than being treated as unanswered.
  values: Object.freeze([
    Object.freeze({ field_id: "field-household-income", subject_id: "household", value: "95000" }),
    Object.freeze({ field_id: "field-monthly-budget", subject_id: "household", value: "150" }),
    Object.freeze({ field_id: "field-goal-of-insurance", subject_id: "household", value: "Protect Family" }),
    Object.freeze({
      field_id: "field-household-notes",
      subject_id: "household",
      value: "Prefers evening callbacks after 6pm.",
    }),
    Object.freeze({ field_id: "field-existing-coverage", subject_id: "prospect-john", value: "100000" }),
    Object.freeze({ field_id: "field-existing-coverage", subject_id: "prospect-mary", value: "0" }),
    Object.freeze({ field_id: "field-tobacco-use", subject_id: "prospect-john", value: "true" }),
    Object.freeze({ field_id: "field-tobacco-use", subject_id: "prospect-mary", value: "false" }),
    Object.freeze({ field_id: "field-annual-income", subject_id: "prospect-john", value: "65000" }),
    Object.freeze({ field_id: "field-annual-income", subject_id: "prospect-mary", value: "42000" }),
    Object.freeze({ field_id: "field-contact-phone", subject_id: "prospect-john", value: "(555) 010-1234" }),
    Object.freeze({ field_id: "field-contact-phone", subject_id: "prospect-mary", value: "(555) 010-5678" }),
    Object.freeze({ field_id: "field-contact-email", subject_id: "prospect-john", value: "john.jason@example.com" }),
  ]),
});

// Deterministic, clearly-synthetic Quote fixture for the Agent Toolbar's
// Quote tool. Mirrors the REAL eventual data contract documented in
// WORKSPACE-AUDIT-004 (docs/audits/workspace/) — the real Script Runner
// Quote Sheet's canonical relationship, not a Workspace-specific invention:
//   - `subjects` uses the same [{household}, ...prospects] shape Discovery
//     already established (WORKSPACE-AUDIT-003) — Quote's real "For:"
//     selector uses this same shape (WORKSPACE-AUDIT-004 SS6). Unlike
//     Discovery, there is no "All" subject — the real Quote Sheet's "For:"
//     selector never has one.
//   - `options` mirrors the real Quote Sheet's fixed Option 1/2/3 model
//     (WORKSPACE-AUDIT-004 SS7) — hardcoded in the real system, not
//     authored, so it is a plain constant array here too.
//   - `fields` mirrors the real `quote_fields` shape
//     (id, label, kind, field_type, sort_order, options,
//     include_in_household) documented in WORKSPACE-AUDIT-004 SS9. This
//     first prototype pass intentionally includes `kind: "input"` fields
//     only — no `kind: "calculated"`/`formula` fields yet (a later,
//     separately authorized pass may add that complexity). There is
//     deliberately no `required` key: the real quote_fields schema has
//     none (WORKSPACE-AUDIT-004 SS9) — required-field UI for Quote is
//     intentionally not implemented in this pass.
//   - `values` mirrors the real `quote_values` shape (an array of
//     {field_id, subject_id, option_index, value} entries) — the same
//     shape the real /runs/{id}/quote_values endpoint returns
//     (WORKSPACE-AUDIT-004 SS8). `subject_id` is always a real prospect
//     id here, never the literal "household" — Household owns no
//     persisted/fixture values of its own; it is derived (WORKSPACE-AUDIT-004
//     SS11/SS20).
// Two synthetic prospects (John Jason, Mary Jason) demonstrate genuine
// N-subject, independent-value, per-option behavior; the rendering logic
// is not written against a fixed count of two or a fixed set of options.
export const QUOTE_FIXTURE = Object.freeze({
  subjects: Object.freeze([
    Object.freeze({ id: "household", name: "Household", kind: "household" }),
    Object.freeze({ id: "prospect-john", name: "John Jason", kind: "prospect", sort_order: 0 }),
    Object.freeze({ id: "prospect-mary", name: "Mary Jason", kind: "prospect", sort_order: 1 }),
  ]),
  options: Object.freeze([1, 2, 3]),
  fields: Object.freeze([
    Object.freeze({
      id: "field-quote-company",
      label: "Company",
      kind: "input",
      field_type: "dropdown",
      sort_order: 1,
      options: ["Carrier A", "Carrier B", "Carrier C"],
      include_in_household: false,
    }),
    Object.freeze({
      id: "field-quote-product",
      label: "Product",
      kind: "input",
      field_type: "dropdown",
      sort_order: 2,
      options: ["Term 20", "Term 30", "Permanent"],
      include_in_household: false,
    }),
    Object.freeze({
      id: "field-quote-coverage",
      label: "Coverage Amount",
      kind: "input",
      field_type: "number",
      sort_order: 3,
      include_in_household: true,
    }),
    Object.freeze({
      id: "field-quote-premium",
      label: "Monthly Premium",
      kind: "input",
      field_type: "number",
      sort_order: 4,
      include_in_household: true,
    }),
  ]),
  // No "household" subject_id ever appears here — Household is derived at
  // render time from the corresponding option's prospect values, never
  // independently stored (WORKSPACE-AUDIT-004 SS11/SS20). With these
  // values, Household derives to: Option 1 -> Coverage 200000, Premium 90;
  // Option 2 -> Coverage 500000, Premium 135; Option 3 -> Coverage
  // 1000000, Premium 220 (Company/Product never appear in Household — they
  // are not include_in_household-eligible).
  values: Object.freeze([
    // John Jason
    Object.freeze({ field_id: "field-quote-company", subject_id: "prospect-john", option_index: 1, value: "Carrier A" }),
    Object.freeze({ field_id: "field-quote-product", subject_id: "prospect-john", option_index: 1, value: "Term 20" }),
    Object.freeze({ field_id: "field-quote-coverage", subject_id: "prospect-john", option_index: 1, value: "100000" }),
    Object.freeze({ field_id: "field-quote-premium", subject_id: "prospect-john", option_index: 1, value: "50" }),
    Object.freeze({ field_id: "field-quote-company", subject_id: "prospect-john", option_index: 2, value: "Carrier A" }),
    Object.freeze({ field_id: "field-quote-product", subject_id: "prospect-john", option_index: 2, value: "Term 20" }),
    Object.freeze({ field_id: "field-quote-coverage", subject_id: "prospect-john", option_index: 2, value: "250000" }),
    Object.freeze({ field_id: "field-quote-premium", subject_id: "prospect-john", option_index: 2, value: "75" }),
    Object.freeze({ field_id: "field-quote-company", subject_id: "prospect-john", option_index: 3, value: "Carrier B" }),
    Object.freeze({ field_id: "field-quote-product", subject_id: "prospect-john", option_index: 3, value: "Permanent" }),
    Object.freeze({ field_id: "field-quote-coverage", subject_id: "prospect-john", option_index: 3, value: "500000" }),
    Object.freeze({ field_id: "field-quote-premium", subject_id: "prospect-john", option_index: 3, value: "120" }),
    // Mary Jason
    Object.freeze({ field_id: "field-quote-company", subject_id: "prospect-mary", option_index: 1, value: "Carrier C" }),
    Object.freeze({ field_id: "field-quote-product", subject_id: "prospect-mary", option_index: 1, value: "Term 20" }),
    Object.freeze({ field_id: "field-quote-coverage", subject_id: "prospect-mary", option_index: 1, value: "100000" }),
    Object.freeze({ field_id: "field-quote-premium", subject_id: "prospect-mary", option_index: 1, value: "40" }),
    Object.freeze({ field_id: "field-quote-company", subject_id: "prospect-mary", option_index: 2, value: "Carrier C" }),
    Object.freeze({ field_id: "field-quote-product", subject_id: "prospect-mary", option_index: 2, value: "Term 30" }),
    Object.freeze({ field_id: "field-quote-coverage", subject_id: "prospect-mary", option_index: 2, value: "250000" }),
    Object.freeze({ field_id: "field-quote-premium", subject_id: "prospect-mary", option_index: 2, value: "60" }),
    Object.freeze({ field_id: "field-quote-company", subject_id: "prospect-mary", option_index: 3, value: "Carrier B" }),
    Object.freeze({ field_id: "field-quote-product", subject_id: "prospect-mary", option_index: 3, value: "Permanent" }),
    Object.freeze({ field_id: "field-quote-coverage", subject_id: "prospect-mary", option_index: 3, value: "500000" }),
    Object.freeze({ field_id: "field-quote-premium", subject_id: "prospect-mary", option_index: 3, value: "100" }),
  ]),
});

// Deterministic, clearly-synthetic Calendar fixture for the Agent Toolbar's
// Calendar tool. Mirrors the REAL eventual data contract documented in
// WORKSPACE-AUDIT-005 (docs/audits/workspace/) — the real `Appointment`
// model's actual fields, not a Workspace-specific invention:
//   - `starts_at`/`ends_at` mirror the real start_at/end_at ISO datetime
//     fields. Timestamps are deliberately naive (no "Z"/offset suffix) so
//     they parse as local time consistently in both the browser and test
//     environments — WORKSPACE-AUDIT-005 SS8/SS21 records live timezone
//     architecture as an unresolved future concern; this fixture keeps
//     formatting local and deterministic without attempting to solve it.
//   - `title` mirrors the real (only) free-text title field — the real
//     model has no separate structured "type" field (WORKSPACE-AUDIT-005
//     SS7/SS12), so title doubles as the description/type line here too.
//   - `lead_name`/`phone` mirror data that in the real system lives on
//     `lead_snapshot.full_name`/`lead_snapshot.phone`, not top-level
//     Appointment fields — flattened here for fixture simplicity, matching
//     the audit's proposed fixture contract (WORKSPACE-AUDIT-005 SS18).
//   - `status` uses ONLY the approved Agent Workspace prototype vocabulary,
//     "Scheduled" | "Completed" — deliberately NOT the approved visual
//     reference image's "Confirmed"/"Pending" labels, which were visual
//     mockup placeholders only and are not part of the real status domain
//     (the real vocabulary is Scheduled | Completed | Rebooked | Ghosted |
//     Sold | Dead Deal — WORKSPACE-AUDIT-005 SS7/SS17).
//   - There is deliberately no separate `duration` field — duration, where
//     shown, is derived from ends_at - starts_at, never stored directly
//     (WORKSPACE-AUDIT-005 SS12/SS17 item 10).
// Appointments intentionally span three different dates and are left in
// non-chronological array order for the initially-selected date, so the
// panel's own filtering/sorting logic (not fixture ordering) is what
// proves correct: Oct 18 (the initial selected date) has three
// appointments demonstrating chronological sort, a Scheduled and a
// Completed status, and one appointment with no ends_at (no fabricated
// duration); Oct 19 has exactly one appointment (singular "1 Appt"
// wording); Oct 20 has none (empty state).
export const CALENDAR_FIXTURE = Object.freeze({
  selectedDate: "2026-10-18",
  appointments: Object.freeze([
    Object.freeze({
      id: "appt-robert-thompson",
      starts_at: "2026-10-18T14:00:00",
      ends_at: null,
      title: "Application Follow-up",
      lead_name: "Robert Thompson",
      phone: null,
      status: "Scheduled",
    }),
    Object.freeze({
      id: "appt-john-dawson",
      starts_at: "2026-10-18T09:00:00",
      ends_at: "2026-10-18T09:30:00",
      title: "Final Expense Consultation",
      lead_name: "John Dawson",
      phone: "(555) 555-0101",
      status: "Scheduled",
    }),
    Object.freeze({
      id: "appt-mary-roberts",
      starts_at: "2026-10-18T11:30:00",
      ends_at: "2026-10-18T12:15:00",
      title: "Policy Review",
      lead_name: "Mary Roberts",
      phone: "(555) 555-0102",
      status: "Completed",
    }),
    Object.freeze({
      id: "appt-karen-wallace",
      starts_at: "2026-10-19T10:00:00",
      ends_at: "2026-10-19T10:30:00",
      title: "Initial Consultation",
      lead_name: "Karen Wallace",
      phone: "(555) 555-0104",
      status: "Scheduled",
    }),
    // Deliberately none on 2026-10-20 -- proves the empty state when that
    // date is selected.
  ]),
});

// Notes tool fixture (WORKSPACE-AUDIT-006). Rehearses the approved product
// direction -- multiple author+timestamped records, not one continuously
// overwritten string -- without claiming this is the finalized backend
// contract (contact_logs is a shape precedent only, not the approved Notes
// backend; see WORKSPACE-AUDIT-006 SS8).
//
// `currentUser` stands in for the authenticated agent until real auth
// context is wired up; newly-added notes in the prototype are attributed to
// this user, never to a fixture-authored note's `created_by_name`.
//
// Existing notes are deliberately left in non-chronological array order (the
// 2026-08-07 09:20 note sits between the two 2026-08-06 notes) so the
// panel's own sort-by-created_at logic -- not fixture ordering -- is what
// proves correct. All three timestamps are fixed in the past relative to
// this fixture's authoring date so that a newly-added note (using the real
// current time by default) always sorts above them without any special-case
// handling.
export const NOTES_FIXTURE = Object.freeze({
  currentUser: Object.freeze({
    id: "agent-spencer-smith",
    name: "Spencer Smith",
  }),
  notes: Object.freeze([
    Object.freeze({
      id: "note-work-coverage",
      body: "Currently has coverage through work but is unsure if it is portable.",
      created_at: "2026-08-06T14:12:00",
      created_by_user_id: "agent-spencer-smith",
      created_by_name: "Spencer Smith",
    }),
    Object.freeze({
      id: "note-mortgage-concern",
      body: "Client is primarily concerned about protecting the mortgage.",
      created_at: "2026-08-06T14:05:00",
      created_by_user_id: "agent-spencer-smith",
      created_by_name: "Spencer Smith",
    }),
    Object.freeze({
      id: "note-coverage-comparison",
      body: "Asked to compare $250,000 and $500,000 coverage options.",
      created_at: "2026-08-07T09:20:00",
      created_by_user_id: "agent-spencer-smith",
      created_by_name: "Spencer Smith",
    }),
  ]),
});

// Deterministic Script Block fixtures. Mirrors the established MissionAssist
// Script Block concept (grouped spoken content, questions, response choices,
// and inputs) so the Workspace Script tab reads as the same product surface
// as Script Runner/Script Builder — never a Workspace-specific invention.
export const SCRIPT_BLOCKS_FIXTURE = Object.freeze({
  scriptType: "FE — Direct Mail v3.1",
  blocks: [
    {
      key: "opening",
      title: "Opening / Introduction",
      lines: [
        "Hi, this is [Agent Name] calling from [Company Name]. How are you today?",
        "May I ask if I'm speaking with [Prospect Name]?",
      ],
      responseLabel: "Prospect response",
      responses: [
        { key: "yes", label: "Yes, this is correct", selected: true },
        { key: "who", label: "Who is this?" },
        { key: "not-interested", label: "Not interested" },
        { key: "callback", label: "Call back later" },
      ],
      acknowledgment: "Great — thanks for confirming.",
    },
    {
      key: "needs-coverage",
      title: "Needs / Coverage",
      lines: [
        "I'm reaching out because many families in your area are reviewing their final expense coverage to help protect their loved ones.",
      ],
      questions: [
        {
          key: "coverage",
          number: 1,
          prompt: "Do you currently have any final expense or burial coverage?",
          required: true,
          type: "choice",
          options: ["Yes, I have coverage", "No, I don't have any", "Not sure", "Other"],
        },
        {
          key: "budget",
          number: 2,
          prompt: "What is the maximum amount you would want to spend monthly?",
          required: true,
          type: "currency",
          placeholder: "Enter amount",
        },
        {
          key: "dob",
          number: 3,
          prompt: "What is your date of birth?",
          required: true,
          type: "date",
          placeholder: "MM / DD / YYYY",
        },
      ],
    },
  ],
  nextSectionLabel: "Need Assessment",
});

// Deterministic, clearly-synthetic Script/Run identity fixture
// (WORKSPACE-AUDIT-007 §13 item 6). Represents the two distinct states
// WORKSPACE-RUNTIME-001 §105 requires the Script tab to model, using the
// same SCRIPT_BLOCKS_FIXTURE content for both -- this fixture supplies
// identity/binding metadata only, never a second set of block content:
//   - `candidate`: minimal representative fields from SCRIPT-RUNTIME-001
//     §36 Assignment Binding ("decision identity... selected Script and
//     publication") -- resolved from Assignment, before any Run exists.
//     No run_id, matching RUN-RUNTIME-001 §34 (run_id is mandatory only
//     for active/resumed execution).
//   - `attached`: minimal representative fields from RUN-RUNTIME-001 §34
//     (immutable run_id) and §37 Publication Binding ("Script,
//     publication, revision, content version, integrity evidence"), plus
//     a static, non-advancing `cursor` -- WORKSPACE-RUNTIME-001 §105's
//     "current Run cursor." No execution, navigation, or response-
//     submission behavior is implemented anywhere in this pass; the
//     cursor is a fixed display value only.
export const SCRIPT_RUN_IDENTITY_FIXTURE = Object.freeze({
  candidate: Object.freeze({
    assignmentDecisionId: "fake-assignment-decision-001",
    scriptId: "script-fe-direct-mail-v3",
    publicationVersion: "3.1",
  }),
  attached: Object.freeze({
    runId: "fake-run-001",
    scriptId: "script-fe-direct-mail-v3",
    publicationVersion: "3.1",
    contentVersion: "content-v3.1.0",
    integrityEvidence: "fake-integrity-sha256-0000000000000000",
    cursor: Object.freeze({ blockKey: "opening", revision: 1 }),
  }),
});
